import React from 'react';
import { Plus, Trash2, Copy } from 'lucide-react';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "./ui/breadcrumb"
import { ChevronRight, Layers, Component, ArrowLeft } from "lucide-react"
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

/**
 * Screen Manager - Manage multiple screens in the onboarding flow
 */
const ScreenManager = ({
  screens,
  activeScreenIndex,
  onSelectScreen,
  onAddScreen,
  onDeleteScreen,
  onDuplicateScreen,
  onSelectElement,
  selectedElement
}) => {
  const [viewMode, setViewMode] = React.useState('screens'); // 'screens' | 'elements'

  // Reset view mode if active screen changes externally (optional, but good for sync)
  // Actually, we might want to stay in elements view if we just switched screens via canvas?
  // Let's keep it simple for now.

  const currentScreen = screens[activeScreenIndex];

  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [screenToDelete, setScreenToDelete] = React.useState(null);

  const handleDeleteClick = (index, e) => {
    e.stopPropagation();
    if (screens.length <= 1) {
      alert('Cannot delete the last screen');
      return;
    }
    setScreenToDelete(index);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (screenToDelete !== null) {
      onDeleteScreen(screenToDelete);
      setDeleteDialogOpen(false);
      setScreenToDelete(null);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Screen?</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this screen? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Header / Breadcrumbs */}
      <div className="p-4 border-b border-border">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink
                className="cursor-pointer flex items-center gap-1"
                onClick={() => setViewMode('screens')}
              >
                <Layers className="h-3 w-3" />
                Screens
              </BreadcrumbLink>
            </BreadcrumbItem>
            {viewMode === 'elements' && (
              <>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage className="flex items-center gap-1">
                    <Component className="h-3 w-3" />
                    {currentScreen?.name || `Screen ${activeScreenIndex + 1}`}
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </>
            )}
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto p-4">
        {viewMode === 'screens' ? (
          <>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-muted-foreground">All Screens</h3>
              <Button variant="ghost" size="icon" onClick={onAddScreen}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="space-y-2">
              {screens.map((screen, index) => {
                const isActive = index === activeScreenIndex;
                return (
                  <Card
                    key={screen.id}
                    className={`cursor-pointer transition-all hover:border-primary/50 group relative ${isActive ? 'border-primary ring-1 ring-primary/20' : ''
                      }`}
                    onClick={() => {
                      onSelectScreen(index);
                      setViewMode('elements');
                    }}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      // Simple confirm for now as a "context menu" action
                      if (window.confirm('Do you want to duplicate this screen?')) {
                        onDuplicateScreen(index);
                      }
                    }}
                  >
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className={`text-sm font-semibold ${isActive ? 'text-primary' : 'text-foreground'
                            }`}>
                            {screen.name || `Screen ${index + 1}`}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-muted-foreground">
                              {(screen.elements || []).length} elements
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {isActive && (
                            <div className="flex items-center gap-1 mr-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 hover:bg-muted"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDuplicateScreen(index);
                                }}
                                title="Duplicate"
                              >
                                <Copy className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-destructive hover:text-destructive hover:bg-destructive/10"
                                onClick={(e) => handleDeleteClick(index, e)}
                                title="Delete"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          )}
                          <ChevronRight className="h-4 w-4 text-muted-foreground/50" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between mb-3">
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2 -ml-2 text-muted-foreground"
                onClick={() => setViewMode('screens')}
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to Screens
              </Button>
            </div>

            <div className="space-y-1">
              {(currentScreen?.elements || []).length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  <p>No elements yet.</p>
                  <p className="text-xs mt-1">Drag components from below to add.</p>
                </div>
              ) : (
                (currentScreen?.elements || []).map((element, idx) => (
                  <Card
                    key={element.id}
                    className={`cursor-pointer transition-all hover:bg-accent ${selectedElement?.id === element.id
                      ? 'bg-accent border-primary'
                      : ''
                      }`}
                    onClick={() => onSelectElement(element)}
                  >
                    <CardContent className="p-3 flex items-center gap-3">
                      <div className="h-8 w-8 rounded bg-muted flex items-center justify-center text-muted-foreground">
                        {/* Simple icon based on type */}
                        {element.type === 'text' && 'T'}
                        {element.type === 'button' && 'B'}
                        {element.type === 'image' && 'I'}
                        {element.type === 'icon' && '★'}
                        {element.type === 'multiple-choice' && '☑'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">
                          {element.name || element.type}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {element.content || element.type}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ScreenManager;
